import type * as THREE from 'three';
import {
  ImportedModelObjectMetadataService,
  type MeasureSelectionTreeHint,
  type Viewer,
} from '@puppycad/cad-core';
import {
  FeatureTreeNodeKind,
  type FeatureTreeNodeMeta,
  type FeatureTreeObjectSummary,
} from './feature-tree-types';
import type { GlbDatumSlotKind } from '@puppycad/cad-core';

/**
 * 测量选中对应的设计树投影结果。
 */
export interface MeasureSelectionTreeProjection {
  /** 应对齐高亮的设计树 key 列表。 */
  treeKeys: string[];
  /** 设计树选中对象摘要。 */
  selectedObject: FeatureTreeObjectSummary | null;
}

/**
 * 设计树 GLB 绑定查询能力。
 */
export interface MeasureSelectionTreeGlbLookup {
  /** 通过 objectId 反查 tree key。 */
  getTreeKeyByObjectId: (objectId: string) => string | undefined;
}

/**
 * 解析测量活动锚点对应的设计树投影。
 *
 * @param options 解析参数。
 * @returns 设计树投影；无法映射时返回 null。
 */
export function resolveMeasureSelectionTreeProjection(options: {
  viewer: Viewer;
  objectId: string;
  hint: MeasureSelectionTreeHint;
  featureTreeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>;
  glbFeatureTree: MeasureSelectionTreeGlbLookup;
}): MeasureSelectionTreeProjection | null {
  const { viewer, objectId, hint, featureTreeNodeMap, glbFeatureTree } = options;

  if (hint.kind === 'unresolvable') {
    return null;
  }

  if (hint.kind === 'datum-plane') {
    const treeKey = `datum-plane-${hint.planeId}`;
    return {
      treeKeys: [treeKey],
      selectedObject: buildDatumPlaneSummary(viewer, hint.planeId),
    };
  }

  if (hint.kind === 'datum-axis') {
    const treeKey = `datum-axis-${hint.axisId}`;
    return {
      treeKeys: [treeKey],
      selectedObject: buildDatumAxisSummary(viewer, hint.axisId),
    };
  }

  if (hint.kind === 'datum-point') {
    const treeKey = hint.isOrigin ? 'origin-node' : `datum-point-${hint.pointId}`;
    return {
      treeKeys: [treeKey],
      selectedObject: buildDatumPointSummary(viewer, hint.pointId),
    };
  }

  if (hint.kind === 'glb-datum-slot') {
    const treeKey = resolveGlbDatumSlotTreeKey(featureTreeNodeMap, hint);
    if (!treeKey) {
      return null;
    }

    const nodeMeta = featureTreeNodeMap.get(treeKey);
    return {
      treeKeys: [treeKey],
      selectedObject: buildGlbDatumSlotSummary(nodeMeta, hint),
    };
  }

  if (hint.kind === 'imported-topology') {
    const sceneObject = viewer.findSceneObjectById(hint.selectionObjectId);
    const treeKey = resolveImportedTopologyTreeKey(sceneObject, glbFeatureTree);
    if (!treeKey) {
      return null;
    }
    const summary = viewer.buildImportedSelectionSummary?.(objectId) ?? null;
    return {
      treeKeys: [treeKey],
      selectedObject: summary?.selectedObject ?? null,
    };
  }

  const nativeTreeKey = resolveNativeTopologyTreeKey(
    featureTreeNodeMap,
    hint.bodyObjectId,
    hint.owningFeatureId,
  );
  if (!nativeTreeKey) {
    return null;
  }

  const nodeMeta = featureTreeNodeMap.get(nativeTreeKey);
  return {
    treeKeys: [nativeTreeKey],
    selectedObject: buildNativeTopologySummary(nodeMeta, hint.bodyObjectId),
  };
}

/**
 * 解析导入模型拓扑对应的设计树 key。
 *
 * @param sceneObject 场景对象。
 * @param glbFeatureTree GLB 绑定查询器。
 * @returns 设计树 key；未命中时返回 null。
 */
function resolveImportedTopologyTreeKey(
  sceneObject: THREE.Object3D | null,
  glbFeatureTree: MeasureSelectionTreeGlbLookup,
): string | null {
  let current: THREE.Object3D | null = sceneObject;
  while (current) {
    const directKey = glbFeatureTree.getTreeKeyByObjectId(current.uuid);
    if (directKey) {
      return directKey;
    }

    const proxyObjectId = ImportedModelObjectMetadataService
      .resolveSelectionProxyObjectId(current)
      ?.trim() ?? '';
    if (proxyObjectId.length > 0) {
      const proxyKey = glbFeatureTree.getTreeKeyByObjectId(proxyObjectId);
      if (proxyKey) {
        return proxyKey;
      }
    }

    current = current.parent;
  }
  return null;
}

/**
 * 解析原生 BRep 拓扑对应的设计树 key。
 *
 * @param featureTreeNodeMap 节点元数据映射。
 * @param bodyObjectId 实体对象 ID。
 * @param owningFeatureId 所属特征 ID。
 * @returns 设计树 key；未命中时返回 null。
 */
function resolveNativeTopologyTreeKey(
  featureTreeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
  bodyObjectId: string,
  owningFeatureId?: string,
): string | null {
  if (owningFeatureId) {
    for (const [treeKey, nodeMeta] of featureTreeNodeMap.entries()) {
      if (nodeMeta.objectId === owningFeatureId) {
        return treeKey;
      }
    }
  }

  const bodyTreeKey = `body-${bodyObjectId}`;
  if (featureTreeNodeMap.has(bodyTreeKey)) {
    return bodyTreeKey;
  }

  for (const [treeKey, nodeMeta] of featureTreeNodeMap.entries()) {
    if (nodeMeta.objectId === bodyObjectId) {
      return treeKey;
    }
  }

  return null;
}

/**
 * 解析 GLB datum-slot 对应的设计树 key。
 *
 * @param featureTreeNodeMap 节点元数据映射。
 * @param hint datum-slot hint。
 * @returns 设计树 key；未命中时返回 null。
 */
function resolveGlbDatumSlotTreeKey(
  featureTreeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
  hint: {
    tabId: string;
    hostObjectId: string;
    slotKind: GlbDatumSlotKind;
  },
): string | null {
  for (const [treeKey, nodeMeta] of featureTreeNodeMap.entries()) {
    if (nodeMeta.kind !== FeatureTreeNodeKind.GlbDatumSlot || !nodeMeta.glbDatumSlot) {
      continue;
    }

    const descriptor = nodeMeta.glbDatumSlot;
    if (
      descriptor.tabId === hint.tabId
      && descriptor.hostObjectId === hint.hostObjectId
      && descriptor.slotKind === hint.slotKind
    ) {
      return treeKey;
    }
  }

  return null;
}

/**
 * 构建 GLB datum-slot 选中摘要。
 *
 * @param nodeMeta 节点元数据。
 * @param hint datum-slot hint。
 * @returns 选中摘要。
 */
function buildGlbDatumSlotSummary(
  nodeMeta: FeatureTreeNodeMeta | undefined,
  hint: { slotKind: GlbDatumSlotKind },
): FeatureTreeObjectSummary | null {
  if (!nodeMeta) {
    return null;
  }

  return {
    id: nodeMeta.objectId ?? hint.slotKind,
    type: 'glb-datum-slot',
    name: nodeMeta.displayName ?? hint.slotKind,
    metadata: {
      type: 'Datum',
      slotKind: hint.slotKind,
    },
  };
}

/**
 * 构建基准面选中摘要。
 *
 * @param viewer Viewer 实例。
 * @param planeId 基准面 ID。
 * @returns 选中摘要。
 */
function buildDatumPlaneSummary(
  viewer: Viewer,
  planeId: number,
): FeatureTreeObjectSummary | null {
  const plane = viewer.getDatumPlaneSummary?.(planeId);
  if (!plane) {
    return null;
  }
  return {
    id: plane.id,
    type: 'datum-plane',
    name: plane.name,
    metadata: {
      type: 'Datum Plane',
    },
  };
}

/**
 * 构建基准轴选中摘要。
 *
 * @param viewer Viewer 实例。
 * @param axisId 基准轴 ID。
 * @returns 选中摘要。
 */
function buildDatumAxisSummary(
  viewer: Viewer,
  axisId: number,
): FeatureTreeObjectSummary | null {
  const axis = viewer.getDatumAxis?.(axisId);
  if (!axis) {
    return null;
  }
  return {
    id: axis.id,
    type: 'datum-axis',
    name: axis.name,
    metadata: {
      type: 'Datum Axis',
    },
  };
}

/**
 * 构建基准点选中摘要。
 *
 * @param viewer Viewer 实例。
 * @param pointId 基准点 ID。
 * @returns 选中摘要。
 */
function buildDatumPointSummary(
  viewer: Viewer,
  pointId: number,
): FeatureTreeObjectSummary | null {
  const point = viewer.getDatumPointSummary?.(pointId);
  if (!point) {
    return null;
  }
  return {
    id: point.id,
    type: 'datum-point',
    name: point.name,
    metadata: {
      type: 'Datum Point',
    },
  };
}

/**
 * 构建原生拓扑所属节点选中摘要。
 *
 * @param nodeMeta 节点元数据。
 * @param bodyObjectId 实体对象 ID。
 * @returns 选中摘要。
 */
function buildNativeTopologySummary(
  nodeMeta: FeatureTreeNodeMeta | undefined,
  bodyObjectId: string,
): FeatureTreeObjectSummary | null {
  if (!nodeMeta) {
    return null;
  }

  if (nodeMeta.objectType === 'body') {
    return {
      id: bodyObjectId,
      type: 'body',
      name: nodeMeta.displayName ?? '实体',
      metadata: {
        type: 'Solid Body',
        createdByFeatureId: nodeMeta.createdByFeatureId,
      },
    };
  }

  if (nodeMeta.objectType === 'feature') {
    return {
      id: nodeMeta.objectId ?? bodyObjectId,
      type: 'feature',
      name: nodeMeta.displayName ?? '特征',
      metadata: {
        type: 'Feature',
        featureKind: nodeMeta.featureKind,
      },
    };
  }

  return null;
}

/**
 * 解析设计树节点对应的可测量场景 objectId。
 *
 * @param nodeMeta 节点元数据。
 * @param viewer Viewer 实例。
 * @returns 可测量 objectId；无法解析时返回 null。
 */
export function resolveFeatureTreeMeasurableObjectId(
  nodeMeta: FeatureTreeNodeMeta,
  viewer: {
    getDatumPlane?: (planeId: number) => { getObject3D: () => THREE.Object3D } | null | undefined;
    getDatumAxis?: (axisId: number) => { getObject3D: () => THREE.Object3D } | null | undefined;
    getDatumPoint?: (pointId: number) => { uuid?: string; getObject3D?: () => THREE.Object3D } | null | undefined;
    findSceneObjectById?: (objectId: string) => THREE.Object3D | null;
  },
): string | null {
  if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && nodeMeta.planeId !== undefined) {
    return viewer.getDatumPlane?.(nodeMeta.planeId)?.getObject3D().uuid ?? null;
  }

  if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId !== undefined) {
    return viewer.getDatumAxis?.(nodeMeta.axisId)?.getObject3D().uuid ?? null;
  }

  if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId !== undefined) {
    const point = viewer.getDatumPoint?.(nodeMeta.pointId);
    if (typeof point?.uuid === 'string') {
      return point.uuid;
    }
    return point?.getObject3D?.().uuid ?? null;
  }

  const objectId = nodeMeta.objectId?.trim() ?? '';
  if (!objectId) {
    return null;
  }

  const sceneObject = viewer.findSceneObjectById?.(objectId) ?? null;
  return sceneObject?.uuid ?? objectId;
}
